<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <div v-if="showStudentSearch && !disabledNavigation" class="pb-4">
      <student-search
        v-model="searchModel"
        @on-close="showStudentSearchHandler"
        @on-search="searchContractHandler"
      />
    </div>
    <general-info
      :contract-ids="contractIds"
      :contract-info="contractInfo"
      :has-next-contract="hasNextContract"
      :has-previous-contract="hasPreviousContract"
      :hidden-navigation="disabledNavigation"
      :is-assistant-service-type="isAssistantServiceType"
      :is-therapy-service-type="isTherapyServiceType"
      :therapy-compensatory-balance="therapyCompensatoryBalance"
      @clear-specialist-dropdown="clearSpecialistDropdown"
      @current="currentContractHandler"
      @next="nextContractHandler"
      @previous="previousContractHandler"
    />
    <div v-if="isTherapyServiceType">
      <intervention-plan
        v-if="!hasFinalizedPeriod"
        :contract-info="contractInfo"
        :document-types="interventionPlan.documentTypes"
        @save-document="saveContractDocument"
        @switch-editing="onSwitchInterventionPlan"
      />
      <div
        v-for="(row, index) of interventionPlan.documents"
        v-show="showInterventionPlan"
        :key="`intervention-plan-item-${index}`"
      >
        <intervention-plan-result-row
          :data="row"
          @donwload-document="downloadDocument"
        />
      </div>
    </div>
    <period-navigation
      ref="periodNavigationRef"
      :completed-period="completedPeriodDate"
      :current-period="selectedPeriodDate(true)"
      :disabled-periods="disabledPeriodDates"
      :extend-period="isExtendAcademicYear"
      :initial-period="initialPeriodDate"
      @input="onChangePeriod"
    />
    <div class="row mt-2">
      <div class="col-md-10" />
      <div class="col-md-2">
        <drop-down
          class="mr-2 d-flex justify-content-end"
          :icon="
            isAscendent ? 'fas fa-sort-numeric-up' : 'fas fa-sort-numeric-down'
          "
          :items="sortBy"
          :title="isAscendent ? 'Ascendente' : 'Descedente'"
        />
      </div>
    </div>
    <visit-registry-add
      v-if="
        (isAssistantServiceType || isTherapyServiceType) &&
          !hasFinalizedPeriod &&
          hasActivePeriod
      "
      ref="visitRegistryAddRef"
      :contract-specialists="specialists"
      :data="simpleContractInfo"
      :default-specialist-id="defaultSpecialistId"
      :fee-schedule-cpt-options="feeScheduleCptOptions"
      :is-therapy-a-b-a="isTherapyABA"
      :request-service-type-id="requestServiceTypeId"
      :selected-period="selectedPeriod"
      :visit-absence-reasons="visitAbsenceReasons"
      :visit-types="visitTypes"
      :visits-valid-for-reposition="visitsValidForReposition"
      @on-save="createVisit"
    />
    <div v-for="(row, index) of results" :key="`contract-item-${index}`">
      <result-row
        :ref="'visitResultRowRef-' + index"
        :contract-specialists="specialists"
        :data="row"
        :fee-schedule-cpt-options="feeScheduleCptOptions"
        :has-finalized-period="hasFinalizedPeriod"
        :is-therapy-a-b-a="isTherapyABA"
        :request-service-type-id="requestServiceTypeId"
        :selected-period="selectedPeriod"
        :simple-contract-info="simpleContractInfo"
        :visit-absence-reasons="visitAbsenceReasons"
        :visits-valid-for-reposition="visitsValidForReposition"
        @delete-visit="deleteVisit"
        @on-download-document="downloadDocument"
        @on-update="(data) => updateVisit(data, index)"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PageHeader from 'PageHeader';
import {
  StudentSearch,
  GeneralInfo,
  VisitRegistryAdd,
  ResultRow,
  InterventionPlan,
  InterventionPlanResultRow,
  PeriodNavigation,
} from './components';
// services
import objectToFormData from 'object-to-formdata';

import {
  getContractById as _getContractById,
  getProviderContractIds as _getProviderContractIds,
  saveContractDocument as _saveContractDocument,
  getContractIdByContractNumber as _getContractIdByContractNumber,
  getBalanceOfTherapyCompensatory as _getBalanceOfTherapyCompensatory,
} from '@/services/uspqrp/contract.api.js';
import {
  searchContractVisits as _searchContractVisits,
  createvisit as _createVisit,
  updateVisit as _updateVisit,
  deleteVisit as _deleteVisit,
  getVisitValidForReposition as _getVisitValidForReposition,
  getVisitValidationInfo as _getVisitValidationInfo,
  getInterventionPlanInfo as _getInterventionPlanInfo,
  getIsValidVisitFrecuency as _getIsValidVisitFrecuency,
  getIsValidVisitAssitantHours as _getIsValidVisitAssitantHours,
  getFeeScheduleCpt,
} from '@/services/uspqrp/visit.api.js';
import { getVisitAbsenceReasons as _getVisitAbsenceReasons } from '@/services/common/code.api.js';

import { getByParameterName } from '@/services/common/sysparameter.api';

import { downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId } from '@/services/common/document.api.js';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import rpServiceTypes from '@/utils/constants/serviceTypes.js';
import rpDisciplineTypes from '@/utils/constants/disciplineTypes.js';
import rpVisitTypes from '@/utils/constants/rpVisitTypes';
import DropDown from 'dropdown';
//constants
const VISIT_REGISTRY_MANAGMENT_ROUTE_NAME = 'visitRegistry';
const PERIOD_NAVIGATION_START_MONTH = 7;
const ASD = 1;
const DSC = 0;
const SAVED_CONFIG_SORT = 'config-option-sort';
export default {
  name: 'VisitRegistryMain',
  mixins: [alertMixin],
  components: {
    PageHeader,
    StudentSearch,
    GeneralInfo,
    VisitRegistryAdd,
    DropDown,
    ResultRow,
    InterventionPlan,
    InterventionPlanResultRow,
    PeriodNavigation,
  },
  data: () => ({
    isAscendent: false,
    contractInfo: {
      contractId: null,
      duration: null,
      includesTransportation: null,
      studentSieId: null,
      studentFullName: null,
      contractNumber: null,
      impediment: null,
      serviceTypeId: null,
      serviceTypeName: null,
      disciplineTypeId: null,
      disciplineTypeName: null,
      frecuencyName: null,
      frecuencyId: null,
      durationId: null,
      durationName: null,
      modalityId: null,
      modalityName: null,
      specialists: [],
      isTherapyCompensatory: null,
      contractStartDate: null,
      ScheduleStartTime: null,
      ScheduleEndTime: null,
      isExtendAcademicYear: false,
    },
    visitTypes: [],
    visitsValidForReposition: [],
    results: [],
    contractIds: [],
    visitAbsenceReasons: [],
    interventionPlan: {
      documents: [],
      documentTypes: [],
    },
    selectedPeriod: null,
    showInterventionPlan: false,
    showStudentSearch: false,
    hasFinalizedPeriod: false,
    completedPeriodDate: null,
    hasActivePeriod: false,
    searchModel: {},
    therapyCompensatoryBalance: null,
    aeeMinDateRange: null,
    aeeMaxDateRange: null,
    feeScheduleCptOptions: [],
  }),
  computed: {
    sortBy() {
      return [
        {
          name: 'Ascendente',
          click: () => this.sortByOption(ASD),
        },
        {
          name: 'Descedente',
          click: () => this.sortByOption(DSC),
        },
      ];
    },
    simpleContractInfo() {
      return {
        contractId: this.contractInfo.contractId,
        duration: this.contractInfo.duration,
        disciplineTypeId: this.contractInfo.disciplineTypeId,
        includesTransportation: this.contractInfo.includesTransportation,
        isTherapyCompensatory: this.contractInfo.isTherapyCompensatory,
        therapyCompensatoryBalance: this.therapyCompensatoryBalance,
        contractStartDate: this.contractInfo.contractStartDate,
        contractSchedule: {
          start: this.contractInfo.scheduleStartTime,
          end: this.contractInfo.scheduleEndTime,
        },
        canEditCpt: this.contractInfo.canEditCpt,
        isExtendedAcademicYear: this.contractInfo.isExtendAcademicYear,
        isCompensatoryRequest: this.contractInfo.isCompensatoryRequest,
        compensatoryRequestId: this.contractInfo.compensatoryRequestId,
        studentSieId: this.contractInfo.studentSieId,
      };
    },
    isExtendAcademicYear() {
      return true; //Every Contract include extend academic year and TC balance
      // return (
      //   this.contractInfo.isExtendAcademicYear ||
      //   this.therapyCompensatoryBalance
      // );
    },
    headerConfig() {
      return {
        title: 'Registro de Visitas',
        icon: 'fas fa-user-tie',
        routes: ['Inicio', 'Proveedor', 'Contrato', 'Registro de Visitas'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Búsqueda de Estudiante',
          icon: 'fas fa-search fa-fw',
          variant: 'outline-success',
          visible: !this.showStudentSearch && !this.disabledNavigation,
          click: () => this.showStudentSearchHandler(),
        },
        {
          text: 'Validar Visitas',
          icon: 'fas fa-check-circle pr-2',
          variant: 'success',
          visible: !this.hasFinalizedPeriod && this.hasActivePeriod,
          click: () => this.onValidateVisitHandler(),
        },
      ];
    },
    requestServiceTypeId() {
      return Number(this.contractInfo?.serviceTypeId);
    },
    requestServiceId() {
      return Number(this.$route.params?.requestServiceId);
    },
    contractId() {
      return Number(this.$route.params?.contractId);
    },
    companyId() {
      return Number(this.$route.params?.companyId);
    },
    academicYearId() {
      return Number(this.$route.params?.academicYearId);
    },
    hasPreviousContract() {
      return Boolean(this.previousContractId);
    },
    hasNextContract() {
      return Boolean(this.nextContractId);
    },
    previousContractId() {
      const currentContractIndex = this.contractIds.findIndex(
        (x) => x === this.contractId
      );
      if (currentContractIndex < 1) return null;
      return this.contractIds[currentContractIndex - 1];
    },
    nextContractId() {
      const currentContractIndex = this.contractIds.findIndex(
        (x) => x === this.contractId
      );
      if (currentContractIndex < 0) return null;
      return this.contractIds[currentContractIndex + 1];
    },
    specialists() {
      return [
        ...this.contractInfo.specialists.filter((x) => !x.finalizedPeriod),
      ];
    },
    defaultSpecialistId() {
      let result = this.specialists.find((x) => x.specialistSupervisorId)
        ?.specialistId;
      if (result) return result;

      let oldestSpecialist = null;
      this.specialists.forEach((specialist, index) => {
        if (index !== 0)
          oldestSpecialist = moment(oldestSpecialist.createDate).isBefore(
            specialist.createDate
          )
            ? oldestSpecialist
            : specialist;
        else oldestSpecialist = specialist;
      });
      return oldestSpecialist?.specialistId;
    },
    isAssistantServiceType() {
      return this.requestServiceTypeId === rpServiceTypes.Assistant;
    },
    isTherapyServiceType() {
      return this.requestServiceTypeId === rpServiceTypes.Therapy;
    },
    isTherapyABA() {
      if (this.isTherapyServiceType)
        return (
          this.contractInfo?.disciplineTypeId === rpDisciplineTypes.TerapiaABA
        );
      return false;
    },
    selectedPeriodDate() {
      return (clicked) => {
        if (!clicked && this.presetMonth) {
          return new Date(new Date().getFullYear(), this.presetMonth, 0);
        } else {
          return this.selectedPeriod
            ? new Date(
                this.selectedPeriod.year,
                this.selectedPeriod.monthNumber,
                0
              )
            : new Date(new Date().getFullYear(), new Date().getMonth(), 0);
        }
      };
    },
    initialPeriodDate() {
      return new Date(
        this.contractInfo.academicYear,
        PERIOD_NAVIGATION_START_MONTH,
        0
      );
    },
    disabledPeriodDates() {
      var currentDate = moment(new Date()).add(1, 'month');
      let stopDate = this.currentAcademicYearInfo?.toDate;
      var dateArray = [];

      if (
        this.contractInfo.isExtendAcademicYear &&
        this.aeeMinDateRange &&
        this.aeeMaxDateRange
      ) {
        stopDate = moment(this.aeeMinDateRange).subtract(1, 'month');
      }

      var stopDateVal = moment(stopDate).add(1, 'month');
      while (currentDate < stopDateVal) {
        dateArray.push(moment(currentDate).toDate());
        currentDate = moment(currentDate).add(1, 'month');
      }

      return dateArray;
    },
    disabledNavigation() {
      return Boolean(this.$route.params?.disableNavigation);
    },
    presetMonth() {
      return Number(this.$route.params?.month);
    },
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.path !== from.path) {
      next();
      await this.onLoad();
    }
  },
  async mounted() {
    this.configSavedValues();
    await this.onLoad();
    await this.evalHasActivePeriod();
    if (this.presetMonth) {
      this.selectedPeriodDate(false);
      this.onChangePeriod({
        year: new Date().getFullYear(),
        monthNumber: this.presetMonth,
      });
    } else {
      this.onChangePeriod({
        year: new Date().getFullYear(),
        monthNumber: this.selectedPeriod.monthNumber,
      });
    }
    await this.getFeeScheduleCpt();
  },
  methods: {
    async onLoad() {
      this.cleanVisitRegistryAddComponent();

      await this.getProviderContractIds();
      await this.searchVisits();
      await Promise.all([
        this.getVisitAbsenceReasons(),
        this.getInterventionPlanInfo(),
      ]);
      await this.getAEEDateRangeSysParam();
    },
    configSavedValues() {
      let opt = sessionStorage.getItem(SAVED_CONFIG_SORT);
      let dsc = DSC.toString();
      if (!opt || opt == dsc) {
        this.isAscendent = DSC;
        return;
      }
      this.isAscendent = ASD;
    },
    sortByOption(val) {
      this.isAscendent = val;
      sessionStorage.setItem(SAVED_CONFIG_SORT, val);
      this.searchVisits();
    },
    async createVisit({ payload, validationPayload }) {
      let isValid = true;
      let msg = '';

      const visitRegistryAddRef = this.$refs.visitRegistryAddRef;

      const currentVisit = Object.fromEntries(payload.entries());
      const visitWithTransportation = this.results.some(
        (visit) =>
          visit.includesTransportation === true &&
          visit.specialistId == currentVisit.specialistId &&
          visit.visitDate === currentVisit.visitDate
      );

      const visitWithTransportationAndSameDate =
        currentVisit.includesTransportationCost === 'true' &&
        visitWithTransportation;

      validationPayload.visitIsCompensatory =
        currentVisit.visitIsCompensatory === 'true';
      validationPayload.visitTypeId = Number(
        currentVisit.visitTypeId || validationPayload.visitTypeId
      );

      if (
        validationPayload.visitTypeId != rpVisitTypes.DiscusionInformeProgreso
      ) {
        if (validationPayload.isTherapyABA) {
          const [hasValidDuration, hasValidFrecuency] = await Promise.all([
            this.getIsValidVisitAssitantHours(validationPayload),
            validationPayload.isReposition ||
              this.getVisitFrecuencyValidation(
                validationPayload.visitDate,
                validationPayload.isAbsence
              ),
          ]);
          if (!hasValidDuration || !hasValidFrecuency) {
            msg = [];
            if (!hasValidDuration)
              msg.push(
                'Visita no cumple con la duración estipulada en el contrato.'
              );
            if (!hasValidFrecuency)
              msg.push(
                'Visita sobrepasa el límite de visitas según la frecuencia del referido.'
              );
            isValid = false;
          }
        } else {
          if (this.isAssistantServiceType) {
            isValid = await this.getIsValidVisitAssitantHours(
              validationPayload
            );
            msg =
              'Visita sobrepasa el límite de horas según su jornada diaria.';
          } else {
            if (!validationPayload.isReposition) {
              isValid = await this.getVisitFrecuencyValidation(
                validationPayload.visitDate,
                validationPayload.isAbsence
              );
              msg =
                'Visita sobrepasa el límite de visitas según la frecuencia del referido.';
            }
          }
        }
      }

      //Validate vigencia del contrato
      ///Pass the visit date, contract_id, visit_ending_date
      if (
        validationPayload.visitDate != null &&
        validationPayload.visitDate > this.contractInfo.visitEndingDate
      ) {
        //Validate if has aee extend year for validate the range
        if (
          this.contractInfo.isExtendAcademicYear &&
          this.ExtendedAcademicYearStartDate &&
          this.ExtendedAcademicYearEndDate
        ) {
          if (
            validationPayload.visitDate < this.ExtendedAcademicYearStartDate ||
            validationPayload.visitDate > this.ExtendedAcademicYearEndDate
          ) {
            msg = 'La visita no puede ser registrada fuera del rango de AEE.';
            isValid = false;
          }
        } else {
          msg = 'La visita no puede ser registrada ya que no cuenta vigencia.';
          isValid = false;
        }
      }

      let visitDate = moment(validationPayload.visitDate).format('YYY-MM-DD');
      let startDate = moment(this.contractInfo.contractStartDate).format(
        'YYY-MM-DD'
      );

      if (validationPayload.visitDate != null && visitDate < startDate) {
        msg =
          'La fecha seleccionada es menor que la fecha comienzo de servicio.';
        isValid = false;
      }

      if (visitWithTransportationAndSameDate) {
        msg = 'Ya existe una visita con transportación en esta fecha.';
        isValid = false;
      }

      if (isValid) {
        await _createVisit(payload)
          .then((data) => {
            data = data.data;
            if (data) {
              this.ShowToast(
                `Confirmación`,
                'La información ha sido guardada exitosamente.',
                'success'
              );
              if (visitRegistryAddRef)
                visitRegistryAddRef.cleanVisitRegistryForm();
            }
          })
          .catch((err) => {
            const errorMsg = err.response?.data?.message;
            this.ShowToast('', errorMsg, 'error');
            if (visitRegistryAddRef && errorMsg) {
              const visitDate = validationPayload.visitDate
                ? moment(validationPayload.visitDate).format('DD-MMM-YYYY')
                : null;
              const timeRange =
                validationPayload.visitTimeStart &&
                validationPayload.visitTimeEnd
                  ? ` (${validationPayload.visitTimeStart} - ${validationPayload.visitTimeEnd})`
                  : '';
              const visitInfo = visitDate
                ? ` — Visita: ${visitDate}${timeRange}`
                : '';
              visitRegistryAddRef.validations.push(`${errorMsg}${visitInfo}`);
            }
          });

        await this.searchVisits();
        await this.getVisitValidForReposition();
        await this.getCompensatoryTherapyBalance();
      } else {
        if (visitRegistryAddRef) {
          if (validationPayload.isTherapyABA) {
            msg.forEach((message) => {
              visitRegistryAddRef.validations.push(message);
            });
          } else {
            visitRegistryAddRef.validations.push(msg);
          }
        }
      }
    },
    async updateVisit({ payload, validationPayload }, index) {
      let isValid = true;
      let msg = '';

      const resultRowRef = this.$refs[`visitResultRowRef-${index}`][0];

      const currentVisit = Object.fromEntries(payload.entries());
      validationPayload.visitTypeId = Number(
        currentVisit.visitTypeId || validationPayload.visitTypeId
      );

      if (this.isAssistantServiceType || validationPayload.isTherapyABA) {
        isValid = await this.getIsValidVisitAssitantHours(validationPayload);
        msg = validationPayload.isTherapyABA
          ? 'Visita no cumple con la duración estipulada en el contrato.'
          : 'Visita sobrepasa el límite de horas según su jornada diaria.';
        if (!isValid && resultRowRef) resultRowRef.validations.push(msg);
      }

      let visitDate = moment(validationPayload.visitDate).format('YYY-MM-DD');
      let startDate = moment(this.contractInfo.contractStartDate).format(
        'YYY-MM-DD'
      );

      if (validationPayload.visitDate != null && visitDate < startDate) {
        if (resultRowRef)
          resultRowRef.validations.push(
            'La fecha seleccionada es menor que la fecha comienzo de servicio.'
          );
      }

      //Validate Visit Shift Duration
      if (this.isAssistantServiceType) {
        if (resultRowRef)
          resultRowRef.validations.push(
            ...(await this.validateVisitShiftDuration(validationPayload))
          );
      }

      isValid = isValid && resultRowRef.validations.length === 0;

      if (isValid) {
        const { data } = await _updateVisit(payload);
        if (data) {
          this.ShowToast(
            `Confirmación`,
            'La información ha sido actualizada exitosamente.',
            'success'
          );
          this.searchVisits();
          this.getVisitValidForReposition();

          if (resultRowRef) resultRowRef.cleanModel();
        }
      }
    },
    async deleteVisit({ id, replacementOfferedFlag }) {
      if (replacementOfferedFlag) {
        this.ShowToast(
          '',
          'La visita no puede ser eliminada, ya que cuenta con una visita de reposición.',
          'error'
        );
        return;
      }

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar la visita seleccionada?`,
        'Sí',
        'No'
      );
      if (!answer) return;
      const { data } = await _deleteVisit(id);
      if (data) {
        this.ShowToast(
          `Confirmación`,
          'La visita ha sido eliminada exitosamente.',
          'success'
        );
        this.searchVisits();
        this.getVisitValidForReposition();
      }
    },
    async getVisitFrecuencyValidation(visitDate, isAbsence) {
      const { data } = await _getIsValidVisitFrecuency(
        this.contractInfo.frecuencyId,
        this.contractId,
        visitDate,
        isAbsence
      );
      return data;
    },
    async getIsValidVisitAssitantHours({
      visitDate,
      visitTimeStart,
      visitTimeEnd,
      visitTimeContinuedStart,
      visitTimeContinuedEnd,
      visitId = 0,
      isTherapyABA = null,
      visitIsCompensatory = null,
      visitTypeId = null,
    }) {
      const { data } = await _getIsValidVisitAssitantHours(
        this.contractId,
        visitDate,
        visitTimeStart,
        visitTimeEnd,
        visitTimeContinuedStart,
        visitTimeContinuedEnd,
        this.contractInfo.studentSieId,
        this.academicYearId,
        visitId,
        isTherapyABA,
        visitIsCompensatory,
        visitTypeId
      );
      return data;
    },
    async getVisitValidForReposition() {
      const { data } = await _getVisitValidForReposition(
        this.academicYearId,
        this.contractId,
        this.contractInfo.studentSieId,
        this.requestServiceTypeId,
        this.contractInfo.disciplineTypeId,
        this.selectedPeriod.year,
        this.selectedPeriod.monthNumber
      );
      this.visitsValidForReposition = data;
    },
    async getVisitAbsenceReasons() {
      const { data } = await _getVisitAbsenceReasons();
      this.visitAbsenceReasons = data;
    },
    async getInterventionPlanInfo() {
      const { data } = await _getInterventionPlanInfo(this.contractId);
      const { documents, documentTypes } = data;
      this.interventionPlan.documents = documents;
      this.interventionPlan.documentTypes = documentTypes;
    },
    async searchVisits() {
      const { data } = await _searchContractVisits(
        this.contractId,
        this.selectedPeriod.academicYearId,
        this.selectedPeriod.monthNumber,
        this.selectedPeriod.year
      );
      if (data) {
        this.results = data.sort((a, b) => {
          if (this.isAscendent) {
            return a.day - b.day;
          }
          return b.day - a.day;
        });
      }
    },
    async getContractById() {
      const { data } = await _getContractById(this.contractId);
      if (data) {
        this.contractInfo = data;
        this.selectedPeriod = {
          academicYearId: this.currentAcademicYearInfo?.id,
          year: new Date().getFullYear(), //data.academicYear,
          monthNumber: new Date().getMonth() + 1, //data.monthNumber,
        };
      }
      await this.getVisitValidForReposition();
      if (this.contractInfo.isTherapyCompensatory) {
        this.getCompensatoryTherapyBalance();
      }
    },
    async getCompensatoryTherapyBalance() {
      const { data } = await _getBalanceOfTherapyCompensatory(this.contractId);
      this.therapyCompensatoryBalance = data;
    },
    async getProviderContractIds() {
      if (this.contractIds.length > 0) {
        await this.getContractById(this.contractId);
        return;
      }
      const { data } = await _getProviderContractIds(this.companyId);
      if (data) {
        this.contractIds = [...data];
        await this.getContractById(this.contractId);
      }
    },
    previousContractHandler() {
      this.results = [];
      this.loadVisitRegistry(this.previousContractId);
    },
    nextContractHandler() {
      this.results = [];
      this.loadVisitRegistry(this.nextContractId);
    },
    currentContractHandler(id) {
      this.loadVisitRegistry(id);
    },
    loadVisitRegistry(contractId) {
      this.$router.replace({
        name: VISIT_REGISTRY_MANAGMENT_ROUTE_NAME,
        params: {
          requestServiceId: this.requestServiceId,
          contractId: contractId,
          companyId: this.companyId,
          academicYearId: this.academicYearId,
        },
      });
    },
    async downloadDocument(documentId) {
      await _downloadDocumentByAttachmentId(documentId);
    },
    async onValidateVisitHandler() {
      const validationResult = (
        await _getVisitValidationInfo(
          this.contractId,
          this.academicYearId,
          this.selectedPeriodDate(true),
          this.contractInfo.specialists[0]?.specialistId,
          this.companyId
        )
      ).data;

      let toastMessage =
        validationResult.errorCount > 0
          ? 'presenta errores'
          : 'se completó exitosamente';

      this.ShowToast(
        'Proceso Exitoso',
        `El proceso de validación ${toastMessage}. Se identificaron ${validationResult.errorCount} error(es)`,
        validationResult.errorCount > 0 ? 'error' : 'success'
      );

      await this.searchVisits();
    },
    cleanVisitRegistryAddComponent() {
      if (this.$refs.visitRegistryAddRef) {
        const visitRegistryAddRef = this.$refs.visitRegistryAddRef;
        visitRegistryAddRef.cleanVisitRegistryForm();
      }
    },
    async saveContractDocument({ contractDocumentTypeId, file }) {
      const payload = objectToFormData(
        {
          contractId: this.contractId,
          academicYearId: this.academicYearId,
          contractDocumentTypeId,
          file,
        },
        { indices: true, nullsAsUndefineds: true }
      );
      await _saveContractDocument(payload);
      await this.getInterventionPlanInfo();
    },
    async getHasFinalizedPeriod() {
      const { data } = await _getContractById(
        this.contractId,
        this.selectedPeriod.monthNumber,
        this.selectedPeriod.year
      );
      if (data) {
        this.contractInfo = data;
        this.selectedPeriod = {
          academicYearId:
            this.academicYearId ?? this.currentAcademicYearInfo?.id,
          year: this.selectedPeriod.year ?? new Date().getFullYear(),
          monthNumber: this.selectedPeriod.monthNumber,
        };
      }

      this.hasFinalizedPeriod =
        this.contractInfo.specialists.filter((x) => x.finalizedPeriod === true)
          .length == this.contractInfo.specialists.length;

      this.completedPeriodDate = this.hasFinalizedPeriod
        ? this.selectedPeriodDate(true)
        : null;
    },
    async evalHasActivePeriod() {
      await this.$nextTick(() => {
        this.hasActivePeriod = this.$refs.periodNavigationRef?.hasActivePeriod;
      });
    },
    async onChangePeriod(value) {
      if (value) {
        this.selectedPeriod = {
          academicYearId: this.academicYearId,
          year: value.year,
          monthNumber: value.monthNumber,
        };
        this.hasActivePeriod = true;
        await this.getHasFinalizedPeriod();
        await this.searchVisits();
      }
    },
    onSwitchInterventionPlan(value) {
      this.showInterventionPlan = value;
    },
    showStudentSearchHandler() {
      this.showStudentSearch = !this.showStudentSearch;
    },
    async searchContractHandler(value) {
      const contractId = (
        await _getContractIdByContractNumber(
          this.companyId,
          value.contractNumber
        )
      ).data;

      if (!contractId) {
        this.ShowToast(
          '',
          'El estudiante no cuenta con un contrato con el proveedor.',
          'error'
        );

        return;
      }

      this.searchModel.contractNumber = null;
      this.loadVisitRegistry(contractId);
      this.showStudentSearchHandler();
    },
    async getAEEDateRangeSysParam() {
      return getByParameterName(
        'SAEEExtendedAcademicYearDateRange',
        this.currentAcademicYearInfo.id
      ).then(({ data }) => {
        if (data) {
          this.aeeMinDateRange = data.rangeMinimumValue;
          this.aeeMaxDateRange = data.rangeMaximumValue;
        }
      });
    },
    async validateVisitShiftDuration(item) {
      const MAX_SHIFT_DURATION_HOURS = 5;
      const validations = [];

      // Asegurarse de que ambos tiempos estén presentes
      if (!item.visitTimeStart || !item.visitTimeEnd) {
        return validations;
      }

      const start = moment(item.visitTimeStart, 'hh:mm A');
      const end = moment(item.visitTimeEnd, 'hh:mm A');

      const durationHours = moment.duration(end.diff(start)).asHours();

      if (durationHours > MAX_SHIFT_DURATION_HOURS) {
        validations.push(
          'El registro de horas no puede superar las 5 horas consecutivas sin tomar una hora para su periodo de alimentación.'
        );
      }

      return validations;
    },
    clearSpecialistDropdown() {
      if (this.$refs.visitRegistryAddRef) {
        this.$refs.visitRegistryAddRef.clearSpecialistDropdown();
      }
    },
    async getFeeScheduleCpt() {
      await getFeeScheduleCpt(this.requestServiceId)
        .then(({ data: res }) => {
          this.feeScheduleCptOptions = res;
        })
        .catch(() => {});
    },
  },
};
</script>
